import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { usePatientList, PatientListItem } from '../hooks/usePatientList';
import EmptyState from '../../../components/ui/EmptyState';
import PermissionGate from '../../../shared/components/PermissionGate';
import ConfirmModal from '../../../components/common/ConfirmModal';
import SkeletonList from '../../../components/ui/SkeletonList';
import { parseApiDate, formatGender } from '../../../lib/format';

const PatientListPage: React.FC = () => {
  const {
    patients,
    loading,
    error,
    pagination,
    searchTerm,
    pendingDeleteId,
    fetchPatients,
    searchPatients,
    deletePatient,
    cancelDelete,
    confirmDelete,
    goToPage,
  } = usePatientList();

  const [search, setSearch] = useState('');

  useEffect(() => {
    fetchPatients();
  }, []);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    searchPatients(search);
  };

  const getStatusBadge = (status: number) => {
    return status === 1 ? (
      <span className="badge bg-success">Activo</span>
    ) : (
      <span className="badge bg-secondary">Inactivo</span>
    );
  };

  const getGenderLabel = (gender: boolean | null | undefined) => formatGender(gender);

  const formatDate = (dateString: string) => {
    if (!dateString) return '-';
    const date = (parseApiDate(dateString) ?? new Date(NaN));
    return date.toLocaleDateString('es-DO');
  };

  return (
    <div className="container-fluid">
      <div className="row">
        <div className="col-md-12">
          <div className="card">
            <div className="card-header">
              <h3 className="card-title">Lista de Pacientes</h3>
              <div className="card-tools">
                <Link to="/patients/new" className="btn btn-primary btn-sm">
                  <i className="fa fa-plus"></i> Nuevo Paciente
                </Link>
              </div>
            </div>

            <div className="card-body">
              {/* Search Form */}
              <div className="row" style={{ marginBottom: '20px' }}>
                <div className="col-md-6">
                  <form onSubmit={handleSearch} className="form-inline">
                    <div className="input-group">
                      <input
                        type="text"
                        className="form-control"
                        placeholder="Buscar por nombre, identificación o expediente..."
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                      />
                      <span className="input-group-btn">
                        <button type="submit" className="btn btn-primary">
                          <i className="fa fa-search"></i>
                        </button>
                      </span>
                    </div>
                  </form>
                </div>
                <div className="col-md-6 text-end">
                  <button
                    type="button"
                    className="btn btn-outline-secondary btn-sm"
                    onClick={() => {
                      setSearch('');
                      fetchPatients(1, '');
                    }}
                  >
                    <i className="fa fa-refresh"></i> Limpiar
                  </button>
                </div>
              </div>

              {error && (
                <div className="alert alert-danger alert-dismissible">
                  <button type="button" className="close" data-dismiss="alert">
                    &times;
                  </button>
                  {error}
                </div>
              )}

              {/* Table */}
              <div className="table-responsive">
                <table className="table table-bordered table-striped">
                  <thead>
                    <tr>
                      <th>Expediente</th>
                      <th>Nombre</th>
                      <th>Identificación</th>
                      <th>Fecha Nac.</th>
                      <th>Género</th>
                      <th>Estado</th>
                      <th>Acciones</th>
                    </tr>
                  </thead>
                   <tbody>
                     {loading ? (
                       <tr>
                         <td colSpan={7} className="p-0">
                           <SkeletonList rowCount={5} columnCount={7} className="py-3" />
                         </td>
                       </tr>
                     ) : patients.length === 0 ? (
                       <EmptyState variant="compact" icon="fa-user" title="No hay pacientes registrados" action={{ label: 'Registrar paciente', to: '/patients/new', icon: 'fa-plus' }} />
                     ) : (
                      patients.map((patient: PatientListItem) => (
                        <tr key={patient.id}>
                          <td>{patient.medical_record_number}</td>
                          <td>
                            {patient.first_name} {patient.last_name}
                          </td>
                          <td>{patient.identification}</td>
                          <td>{formatDate(patient.birth_date)}</td>
                          <td>{getGenderLabel(patient.gender)}</td>
                          <td>{getStatusBadge(patient.status)}</td>
                          <td>
                            <div className="btn-group" role="group" aria-label={`Acciones del paciente ${patient.first_name} ${patient.last_name}`}>
                              <Link
                                to={`/patients/${patient.id}`}
                                className="action-btn action-btn-view"
                                title="Ver"
                                aria-label={`Ver paciente ${patient.first_name} ${patient.last_name}`}
                              >
                                <i className="fa fa-eye" aria-hidden="true"></i>
                              </Link>
                              <Link
                                to={`/patients/${patient.id}/edit`}
                                className="btn btn-primary btn-xs"
                                title="Editar"
                                aria-label={`Editar paciente ${patient.first_name} ${patient.last_name}`}
                              >
                                <i className="fa fa-edit" aria-hidden="true"></i>
                              </Link>
                              <PermissionGate module="patients" action="delete">
                                <button
                                  className="btn btn-danger btn-xs"
                                  title="Eliminar"
                                  aria-label={`Eliminar paciente ${patient.first_name} ${patient.last_name}`}
                                  onClick={() => deletePatient(patient.id)}
                                >
                                  <i className="fa fa-trash" aria-hidden="true"></i>
                                </button>
                              </PermissionGate>
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {!searchTerm && pagination.totalPages > 1 && (
                <div className="row">
                  <div className="col-md-6">
                    <div className="dataTables_info">
                      Mostrando {pagination.currentPage} de {pagination.totalPages} páginas
                      (Total: {pagination.total} registros)
                    </div>
                  </div>
                  <div className="col-md-6">
                    <ul className="pagination pagination-sm float-end">
                      <li className={pagination.currentPage === 1 ? 'disabled' : ''}>
                        <button
                          className="btn btn-outline-secondary btn-xs"
                          onClick={() => goToPage(1)}
                          disabled={pagination.currentPage === 1}
                        >
                          &laquo;
                        </button>
                      </li>
                      {(() => {
                        const current = pagination.currentPage;
                        const total = pagination.totalPages;
                        const delta = 2; // pages around current
                        const range: (number | '...')[] = [];
                        for (
                          let i = Math.max(2, current - delta);
                          i <= Math.min(total - 1, current + delta);
                          i++
                        ) {
                          range.push(i);
                        }
                        if (current - delta > 2) range.unshift('...');
                        if (current + delta < total - 1) range.push('...');
                        range.unshift(1);
                        if (total > 1) range.push(total);

                        return range.map((page, idx) =>
                          page === '...' ? (
                            <li key={`ellipsis-${idx}`} className="disabled">
                              <span className="btn btn-outline-secondary btn-xs">...</span>
                            </li>
                          ) : (
                            <li key={page} className={current === page ? 'active' : ''}>
                              <button
                                className="btn btn-outline-secondary btn-xs"
                                onClick={() => goToPage(page as number)}
                              >
                                {page}
                              </button>
                            </li>
                          )
                        );
                      })()}
                      <li className={pagination.currentPage === pagination.totalPages ? 'disabled' : ''}>
                        <button
                          className="btn btn-outline-secondary btn-xs"
                          onClick={() => goToPage(pagination.totalPages)}
                          disabled={pagination.currentPage === pagination.totalPages}
                        >
                          &raquo;
                        </button>
                      </li>
                    </ul>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <ConfirmModal
        show={pendingDeleteId !== null}
        title="Eliminar paciente"
        body="¿Está seguro que desea eliminar este paciente? Esta acción no se puede deshacer."
        confirmLabel="Eliminar"
        confirmVariant="danger"
        loading={loading}
        onConfirm={confirmDelete}
        onCancel={cancelDelete}
      />
    </div>
  );
};

export default PatientListPage;
